import { type FormEvent, useCallback, useEffect, useState } from "react";

import { type AdminMember, adminApi } from "@/lib/adminApi";
import { ActionRow, PanelSection, StatusBanner } from "@/components/ui";
import { useSaveState } from "@/lib/useSaveState";

type Draft = { username: string; display_name: string; role: string; live: boolean };

const USERNAME_RULE = /^[a-z0-9_-]{1,64}$/;

function toDraft(member: AdminMember): Draft {
  return {
    username: member.username,
    display_name: member.display_name ?? "",
    role: member.role ?? "",
    live: member.live,
  };
}

/** Blank optional fields are sent as absent rather than empty strings. */
function toPayload(draft: Draft) {
  return {
    username: draft.username.trim().toLowerCase(),
    display_name: draft.display_name.trim() || null,
    role: draft.role.trim() || null,
  };
}

function validate(drafts: readonly Draft[]): Map<number, string> {
  const errors = new Map<number, string>();
  const seen = new Map<string, number>();

  drafts.forEach((draft, index) => {
    const name = draft.username.trim().toLowerCase();
    if (!name) {
      errors.set(index, "A TETR.IO username is required.");
      return;
    }
    if (!USERNAME_RULE.test(name)) {
      errors.set(index, "Letters, digits, dashes and underscores only.");
      return;
    }
    const first = seen.get(name);
    if (first !== undefined) {
      errors.set(index, `Already listed on row ${first + 1}.`);
      return;
    }
    seen.set(name, index);
  });

  return errors;
}

interface RosterPanelProps {
  readonly onUnauthenticated: () => void;
}

export function RosterPanel({ onUnauthenticated }: RosterPanelProps) {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  // Until TETR.IO has actually been queried, "no live stats" would be a lie.
  const [statsChecked, setStatsChecked] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const save = useSaveState(onUnauthenticated);
  const refresh = useSaveState(onUnauthenticated);

  const reload = useCallback(async () => {
    try {
      const body = await adminApi.roster();
      setDrafts(body.members.map(toDraft));
      setStatsChecked(body.stats_checked);
      setLoadError(null);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "Could not load the roster.");
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const errors = validate(drafts);

  const update = (index: number, changes: Partial<Draft>) =>
    setDrafts(drafts.map((draft, i) => (i === index ? { ...draft, ...changes } : draft)));

  const addRow = () =>
    setDrafts([...drafts, { username: "", display_name: "", role: "", live: false }]);

  const removeRow = (index: number) => setDrafts(drafts.filter((_, i) => i !== index));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (errors.size > 0) return;
    const done = await save.run(
      () => adminApi.saveRoster(drafts.map(toPayload)),
      (result) => result.message,
    );
    if (done) await reload();
  };

  const refreshStats = async () => {
    const done = await refresh.run(
      () => adminApi.refreshRoster(),
      (result) => result.message,
    );
    if (done) await reload();
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      <PanelSection
        title={`Members (${drafts.length})`}
        description="Add a TETR.IO username and their stats appear on the site automatically. Display name and role are optional."
      >
        {!statsChecked ? (
          <p className="mb-4 text-[0.875rem] leading-relaxed opacity-60">
            Stats have not been fetched since the server started. Use “Refresh stats now” to
            check every username against TETR.IO.
          </p>
        ) : null}
        <StatusBanner tone="error" message={loadError} />

        <div className="mt-4 flex flex-col gap-2">
          <div className="hidden gap-3 px-1 sm:grid sm:grid-cols-[1fr_1fr_10rem_5rem]">
            <span className="label mb-0">TETR.IO username</span>
            <span className="label mb-0">Display name</span>
            <span className="label mb-0">Role</span>
            <span className="label mb-0 text-right">Remove</span>
          </div>

          {drafts.map((draft, index) => {
            const error = errors.get(index);
            return (
              <div
                key={index}
                className="grid gap-3 border-2 border-ink px-3 py-3 sm:grid-cols-[1fr_1fr_10rem_5rem]
                  sm:items-start sm:border-0 sm:px-1 sm:py-0"
              >
                <div>
                  <label className="label sm:hidden" htmlFor={`member-${index}`}>
                    TETR.IO username
                  </label>
                  <input
                    id={`member-${index}`}
                    className="input input--mono"
                    autoCapitalize="none"
                    spellCheck={false}
                    aria-invalid={error ? "true" : undefined}
                    aria-label={`Username, row ${index + 1}`}
                    value={draft.username}
                    onChange={(event) => update(index, { username: event.target.value })}
                  />
                  {error ? (
                    <p className="mt-1 text-[0.8125rem] font-semibold text-piece-z">{error}</p>
                  ) : statsChecked && draft.username && !draft.live ? (
                    <p className="mt-1 text-[0.8125rem] opacity-60">
                      TETR.IO did not return stats — check the spelling, or they have no
                      ranked games.
                    </p>
                  ) : null}
                </div>

                <div>
                  <label className="label sm:hidden" htmlFor={`display-${index}`}>
                    Display name
                  </label>
                  <input
                    id={`display-${index}`}
                    className="input"
                    placeholder={draft.username || "same as username"}
                    value={draft.display_name}
                    onChange={(event) => update(index, { display_name: event.target.value })}
                  />
                </div>

                <div>
                  <label className="label sm:hidden" htmlFor={`role-${index}`}>
                    Role
                  </label>
                  <input
                    id={`role-${index}`}
                    className="input"
                    placeholder="President"
                    maxLength={48}
                    value={draft.role}
                    onChange={(event) => update(index, { role: event.target.value })}
                  />
                </div>

                <button
                  type="button"
                  className="btn btn--ghost h-fit justify-center px-3 py-2 text-xs sm:mt-0"
                  onClick={() => removeRow(index)}
                  aria-label={`Remove ${draft.username || `row ${index + 1}`}`}
                >
                  Remove
                </button>
              </div>
            );
          })}
        </div>

        <button type="button" className="btn btn--ghost mt-4" onClick={addRow}>
          + Add member
        </button>
      </PanelSection>

      <StatusBanner tone={save.tone} message={save.message} />
      <StatusBanner tone={refresh.tone} message={refresh.message} />

      <ActionRow>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => void refreshStats()}
          disabled={refresh.saving}
        >
          {refresh.saving ? "Fetching…" : "Refresh stats now"}
        </button>
        <button type="submit" className="btn" disabled={save.saving || errors.size > 0}>
          {save.saving ? "Saving…" : "Save roster"}
        </button>
      </ActionRow>

      {errors.size > 0 ? (
        <p role="alert" className="text-right text-[0.875rem] font-semibold text-piece-z">
          Fix {errors.size} {errors.size === 1 ? "row" : "rows"} before saving.
        </p>
      ) : null}
    </form>
  );
}
